import { useAppDispatch, useAppSelector } from "@/app/hooks";
import type { Notification } from "@/features/notifier/notification";
import {
  removeSnackbar,
  selectNotifications,
} from "@/features/notifier/notifierSlice";
import { type SnackbarKey, SnackbarProvider, useSnackbar } from "notistack";
import React from "react";
import { useEffect } from "react";

type Props = {
  children: React.ReactNode;
};

export default function Notifier({ children }: Props) {
  return (
    <SnackbarProvider
      maxSnack={3}
      anchorOrigin={{
        vertical: "top",
        horizontal: "center",
      }}
      style={{
        marginTop: "70px",
      }}
    >
      <NotificationRenderer />

      {children}
    </SnackbarProvider>
  );
}

function NotificationRenderer() {
  const dispatch = useAppDispatch();
  const notifications = useAppSelector(selectNotifications);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const displayedRef = React.useRef<Set<SnackbarKey>>(new Set());

  useEffect(() => {
    const storeDisplayed = (key: SnackbarKey) => {
      displayedRef.current.add(key);
    };

    const removeDisplayed = (key: SnackbarKey) => {
      displayedRef.current.delete(key);
    };

    for (const notification of notifications) {
      try {
        const {
          message,
          options = {},
          dismissed = false,
        } = notification as Notification;

        const key = (options as { key?: SnackbarKey }).key;

        if (!key) {
          console.warn("Notification missing key:", notification);
          continue;
        }

        if (dismissed) {
          closeSnackbar(key);
          continue;
        }

        if (displayedRef.current.has(key)) {
          continue;
        }

        enqueueSnackbar(message, {
          ...options,
          key,
          onClose: (event, reason, snackbarKey) => {
            options.onClose?.(event, reason, snackbarKey);
          },
          onExited: (event, snackbarKey) => {
            dispatch(removeSnackbar(snackbarKey));
            removeDisplayed(snackbarKey);
            options.onExited?.(event, snackbarKey);
          },
        });

        storeDisplayed(key);
      } catch (error) {
        console.error("Error processing notification:", error, notification);
      }
    }
  }, [notifications, closeSnackbar, enqueueSnackbar, dispatch]);

  return null;
}
